import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import InputField from "../../../../components/InputField";
import SelectField from "../../../../components/SelectField";
import Button from "../../../../components/Button";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import "../../Common/form.css";
import Select from "react-select";
import api from "../../../../api/api";

import { useAuth } from "../../../../context/AuthContext";
import BackButton from "../../../../components/BackButton";
import Loader from "../../../../components/Loader";

const SendQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Lock-related state
  const [isLocked, setIsLocked] = useState(false);
  const [lockError, setLockError] = useState(null);
  const [lockedByName, setLockedByName] = useState(null);
  const [lockedBy, setLockedBy] = useState(null);
  const [checkingLock, setCheckingLock] = useState(true);

  // Helper functions for date formatting
  const formatDateToDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseDDMMYYYYToYYYYMMDD = (dateStr) => {
    if (!dateStr) return "";
    const [day, month, year] = dateStr.split("-");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  const [ports, setPorts] = useState([]);
  const [loadingPorts, setLoadingPorts] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [chargeCodeOptions, setChargeCodeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [loadingChargeCodes, setLoadingChargeCodes] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [loadingEnquiry, setLoadingEnquiry] = useState(true);
  const [loading, setLoading] = useState(true);

  const [inputRow, setInputRow] = useState({
    containerType: "",
    weight: "",
    length: "",
    breadth: "",
    height: "",
    temperature: "",
  });
  const [inputChargeRow, setInputChargeRow] = useState({
    charge_code: "",
    currency: "",
    amount: 0,
    gst: 0,
    quantity: 0,
    tax: 0,
    gst_amount: 0,
    cgst: 0,
    sgst: 0,
    total: 0,
    exchange_rate: 0,
  });
  const [chargeRow, setChargeRow] = useState([]);

  const [totals, setTotals] = useState({
    subTotal: 0,
    gstAmount: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    grandTotal: 0,
  });

  const [formData, setFormData] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [errors, setErrors] = useState({
    form: {},
    containerTable: "",
    chargeTable: "",
    selectedLocation: "",
  });
  const [error, setError] = useState(null);
  const [emailRecipients, setEmailRecipients] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [quotationPdf, setQuotationPdf] = useState(null);

  const incoterms = [
    "Port to Port",
    "Door to Door",
    "Door to Port",
    "Port to Door",
    "ICD to Port",
    "ICD to Door",
  ];
  const shipment_segments = ["FE", "LE", "LI", "FI"];
  const freight_terms = ["Prepaid", "Collect"];

  // Location options for job number generation
  const locationOptions = [
    { value: "07", label: "Delhi (07)" },
    { value: "27", label: "Mumbai (27)" },
    { value: "33", label: "Chennai (33)" },
  ];
  const routing_options = ["Direct", "Indirect", "Both"];

  const [selectedLocation, setSelectedLocation] = useState(null);

  // Lock management functions
  const checkAndLockEnquiry = async () => {
    try {
      setCheckingLock(true);
      // First try to lock the enquiry
      const lockResponse = await api.post(`/quotation/${id}/lock`);
      if (lockResponse.data.success) {
        setIsLocked(false); // Successfully locked, so not locked by others
        setLockError(null);
      }
    } catch (lockErr) {
      if (lockErr.response?.status === 409) {
        setIsLocked(true);
        setLockedByName(lockErr.response?.data?.lockedByName || null);
        setLockedBy(lockErr.response?.data?.lockedBy || null);
        setLockError("Looks like someone from our team is working on it");
      } else {
        setLockError("Failed to check lock status. Please try again.");
      }
    } finally {
      setCheckingLock(false);
    }
  };

  const unlockEnquiry = async () => {
    try {
      await api.delete(`/quotation/${id}/unlock`);
    } catch (err) {
      console.error("Failed to unlock enquiry:", err);
      // Don't show error to user as this happens on unmount
    }
  };

  // Handle beforeunload to unlock when user closes/refreshes
  useEffect(() => {
    const handleBeforeUnload = () => {
      unlockEnquiry();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      unlockEnquiry(); // Also unlock on component unmount
    };
  }, [id]);

  useEffect(() => {
    if (
      !loadingPorts &&
      !loadingChargeCodes &&
      !loadingCurrencies &&
      !loadingEnquiry &&
      !checkingLock
    ) {
      setLoading(false);
    }
  }, [loadingPorts, loadingChargeCodes, loadingCurrencies, loadingEnquiry, checkingLock]);

  useEffect(() => {
    const fetchChargeCodes = async () => {
      try {
        setLoadingChargeCodes(true);
        const response = await api.get("/charge_code/options");
        // console.log("response.data", response.data);

        setChargeCodeOptions(response.data);
      } catch (err) {
        console.error("Failed to fetch charge codes:", err);
      } finally {
        setLoadingChargeCodes(false);
      }
    };

    fetchChargeCodes();
  }, []);

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        setLoadingCurrencies(true);
        const response = await api.get("/exchange_rate");
        // console.log("response.data exchange_rate", response.data);

        const formatted = response.data.map((item) => ({
          value: item.country_code,
          label: item.country_code, // or a more friendly name if you have it
        }));
        // console.log("formatted", formatted);

        setCurrencyOptions(formatted);
      } catch (err) {
        console.error("Failed to fetch exchange rates:", err);
      } finally {
        setLoadingCurrencies(false);
      }
    };

    fetchExchangeRates();
  }, []);

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        setLoadingPorts(true);
        const response = await api.get("/ports");
        const allPorts = response.data;
        setPorts(allPorts);
      } catch (err) {
        console.error("Failed to fetch ports:", err);
      } finally {
        setLoadingPorts(false);
      }
    };

    fetchPorts();
  }, []);

  const fetchGSTForChargeCode = async (charge_code, index) => {
    if (!charge_code) return;

    try {
      const response = await api.get(`/charge_code/gst/${charge_code}`);
      const gstPercentage = response.data.tax_percentage || 0;

      if (index === "input") {
        setInputChargeRow((prev) => {
          const updated = { ...prev, gst: gstPercentage };

          // Recalc
          const amt = parseFloat(updated.amount) || 0;
          const qty = parseFloat(updated.quantity) || 0;
          const exchange = parseFloat(updated.exchange_rate) || 1;

          const taxAmount = amt * qty * exchange;
          const gstAmount = (taxAmount * gstPercentage) / 100;
          const total = taxAmount + gstAmount;

          updated.tax = Number(taxAmount.toFixed(2));
          updated.gst_amount = Number(gstAmount.toFixed(2));
          updated.cgst = Number((gstAmount / 2).toFixed(2));
          updated.sgst = Number((gstAmount / 2).toFixed(2));
          updated.total = Number(total.toFixed(2));

          return updated;
        });
      } else {
        setChargeRow((prev) => {
          const updated = [...prev];
          updated[index].gst = gstPercentage;

          // Optionally recalc gst_amount and total if needed
          const amount = parseFloat(updated[index].amount) || 0;
          const qty = parseFloat(updated[index].quantity) || 0;
          const exchange = parseFloat(updated[index].exchange_rate) || 1;

          const taxAmount = amount * qty * exchange;
          const gstAmount = (taxAmount * gstPercentage) / 100;
          const total = taxAmount + gstAmount;

          updated[index].gst_amount = Number(gstAmount.toFixed(2));
          updated[index].cgst = Number((gstAmount / 2).toFixed(2));
          updated[index].sgst = Number((gstAmount / 2).toFixed(2));
          updated[index].tax = Number(taxAmount.toFixed(2));
          updated[index].total = Number(total.toFixed(2));

          return updated;
        });
      }
    } catch (err) {
      console.error("Failed to fetch GST for charge code:", err);
    }
  };
  const handleCurrencyChange = async (index, selectedCurrency) => {
    const country_code = selectedCurrency?.value || "";
    if (index === "input") {
      handleInputChargeChange("currency", country_code);
    } else {
      handleChargeInputChange(index, "currency", country_code);
    }

    if (!country_code) {
      if (index === "input") {
        handleInputChargeChange("exchange_rate", "");
      } else {
        handleChargeInputChange(index, "exchange_rate", "");
      }
      return;
    }
    // console.log("country_code", country_code);

    try {
      const res = await api.get(`/exchange_rate/rate/${country_code}`);
      // console.log("res", res);
      if (index === "input") {
        handleInputChargeChange("exchange_rate", Number(res.data.rate_in_INR));
      } else {
        handleChargeInputChange(
          index,
          "exchange_rate",
          Number(res.data.rate_in_INR)
        );
      }
    } catch (err) {
      console.error("Failed to fetch exchange rate:", err);
      if (index === "input") {
        handleInputChargeChange("exchange_rate", "N/A");
      } else {
        handleChargeInputChange(index, "exchange_rate", "N/A");
      }
    }
  };

  const fetchEnquiry = async () => {
    try {
      const res = await api.get(`/enquiry/viewEnquiry/${id}`);
      // Set creation_date to current date
      const enquiryData = {
        ...res.data,
        creation_date: new Date().toISOString().split('T')[0] // YYYY-MM-DD format
      };
      setFormData(enquiryData);

      // Initialize selectedLocation from saved job_location
      if (enquiryData.job_location) {
        const savedLocation = locationOptions.find(opt => opt.value === enquiryData.job_location);
        if (savedLocation) {
          setSelectedLocation(savedLocation);
        } else {
          setSelectedLocation({ value: enquiryData.job_location, label: enquiryData.job_location });
        }
      }

      setTableData(
        res.data.containerDetails.map((container) => ({
          container_id: container.container_id,
          containerType: container.container_type,
          weight: container.container_weight,
          length: container.container_length,
          breadth: container.container_breadth,
          height: container.container_height,
          temperature: container.container_temperature,
        }))
      );
      setLoadingEnquiry(false);
    } catch (err) {
      console.error("Failed to fetch enquiry:", err);
      setError(
        "Failed to load enquiry data. Please check the enquiry ID or try again."
      );
      setLoadingEnquiry(false);
    }
  };

  useEffect(() => {
    fetchEnquiry();
  }, [id]);

  // Check lock after enquiry is loaded
  useEffect(() => {
    if (!loadingEnquiry && formData) {
      checkAndLockEnquiry();
    }
  }, [loadingEnquiry, formData, id]);

  // Sync selectedLocation from formData.job_location
  useEffect(() => {
    if (formData?.job_location && !selectedLocation) {
      const savedLocation = locationOptions.find(opt => opt.value === formData.job_location);
      if (savedLocation) {
        setSelectedLocation(savedLocation);
      } else if (formData.job_location) {
        setSelectedLocation({ value: formData.job_location, label: formData.job_location });
      }
    }
  }, [formData]);

  const portOptions = Array.isArray(ports)
    ? ports.map((port) => port.Port_names)
    : [];

  // ---------------- Field Change Handlers ----------------
  const handleInputChange = (field, value) => {
    setInputRow((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };
  const handleInputChargeChange = (field, value) => {
    setInputChargeRow((prev) => {
      const updated = { ...prev, [field]: value };

      // Calculate tax, gst_amount, total
      const amt = parseFloat(updated.amount) || 0;
      const qty = parseFloat(updated.quantity) || 0;
      const gstRate = parseFloat(updated.gst) || 0;
      const exchange = parseFloat(updated.exchange_rate) || 1;

      const taxAmount = amt * qty * exchange;
      const gstAmount = (taxAmount * gstRate) / 100;
      const total = taxAmount + gstAmount;

      updated.tax = Number(taxAmount.toFixed(2));
      updated.gst_amount = Number(gstAmount.toFixed(2));
      updated.cgst = Number((gstAmount / 2).toFixed(2));
      updated.sgst = Number((gstAmount / 2).toFixed(2));
      updated.total = Number(total.toFixed(2));

      return updated;
    });
    calculateTotals();
  };
  const calculateTotals = () => {
    const subTotal =
      (Number(inputChargeRow.tax) || 0) +
      chargeRow.reduce((acc, row) => acc + Number(row.tax || 0), 0);
    const gstAmount =
      (Number(inputChargeRow.gst_amount) || 0) +
      chargeRow.reduce((acc, row) => acc + Number(row.gst_amount || 0), 0);
    const cgstAmount =
      (Number(inputChargeRow.cgst) || 0) +
      chargeRow.reduce((acc, row) => acc + Number(row.cgst || 0), 0);
    const sgstAmount =
      (Number(inputChargeRow.sgst) || 0) +
      chargeRow.reduce((acc, row) => acc + Number(row.sgst || 0), 0);
    const grandTotal = subTotal + gstAmount;

    setTotals({
      subTotal,
      cgstAmount,
      sgstAmount,
      gstAmount,
      grandTotal,
    });
  };

  // call this only when you click "addChargeRow"
  const addChargeRow = () => {
    if (
      !inputChargeRow.charge_code ||
      !inputChargeRow.currency ||
      !inputChargeRow.amount ||
      !inputChargeRow.quantity
    ) {
      setErrors((prev) => ({
        ...prev,
        chargeTable:
          "Please fill all required fields in the input row before adding a new row",
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, chargeTable: "" }));

    const newCharge = { ...inputChargeRow };

    setChargeRow((prev) => {
      const updated = [...prev, newCharge];

      // Calculate totals with updated chargeRow and reset inputChargeRow (which is now 0)
      const subTotal =
        0 + updated.reduce((acc, row) => acc + Number(row.tax || 0), 0);
      const gstAmountTotal =
        0 + updated.reduce((acc, row) => acc + Number(row.gst_amount || 0), 0);
      const cgstAmount =
        0 + updated.reduce((acc, row) => acc + Number(row.cgst || 0), 0);
      const sgstAmount =
        0 + updated.reduce((acc, row) => acc + Number(row.sgst || 0), 0);
      const grandTotal = subTotal + gstAmountTotal;

      setTotals({
        subTotal,
        cgstAmount,
        sgstAmount,
        gstAmount: gstAmountTotal,
        grandTotal,
      });

      return updated;
    });

    // Reset inputChargeRow
    setInputChargeRow({
      charge_code: "",
      currency: "",
      amount: 0,
      gst: 0,
      quantity: 0,
      tax: 0,
      gst_amount: 0,
      cgst: 0,
      sgst: 0,
      total: 0,
      exchange_rate: 0,
    });
  };

  const handleChargeInputChange = (index, field, value) => {
    setChargeRow((prev) => {
      const updated = [...prev];
      updated[index][field] = value;

      const amt = parseFloat(updated[index].amount) || 0;
      const qty = parseFloat(updated[index].quantity) || 0;
      const gstRate = parseFloat(updated[index].gst) || 0;
      const exchange = parseFloat(updated[index].exchange_rate) || 1;

      const taxAmount = amt * qty * exchange;
      const gstAmount = (taxAmount * gstRate) / 100;
      const total = taxAmount + gstAmount;

      updated[index].tax = Number(taxAmount.toFixed(2));
      updated[index].gst_amount = Number(gstAmount.toFixed(2));
      updated[index].cgst = Number((gstAmount / 2).toFixed(2));
      updated[index].sgst = Number((gstAmount / 2).toFixed(2));
      updated[index].total = Number(total.toFixed(2));

      // Calculate totals with updated
      const subTotal =
        (Number(inputChargeRow.tax) || 0) +
        updated.reduce((acc, row) => acc + Number(row.tax || 0), 0);
      const gstAmountTotal =
        (Number(inputChargeRow.gst_amount) || 0) +
        updated.reduce((acc, row) => acc + Number(row.gst_amount || 0), 0);
      const cgstAmount =
        (Number(inputChargeRow.cgst) || 0) +
        updated.reduce((acc, row) => acc + Number(row.cgst || 0), 0);
      const sgstAmount =
        (Number(inputChargeRow.sgst) || 0) +
        updated.reduce((acc, row) => acc + Number(row.sgst || 0), 0);
      const grandTotal = subTotal + gstAmountTotal;

      setTotals({
        subTotal,
        cgstAmount,
        sgstAmount,
        gstAmount: gstAmountTotal,
        grandTotal,
      });

      return updated;
    });
  };

  const deleteChargeRow = (index) => {
    setChargeRow((prev) => {
      const updatedCharges = prev.filter((_, i) => i !== index);
      // Calculate totals with updatedCharges
      const subTotal =
        (Number(inputChargeRow.tax) || 0) +
        updatedCharges.reduce((acc, row) => acc + Number(row.tax || 0), 0);
      const gstAmount =
        (Number(inputChargeRow.gst_amount) || 0) +
        updatedCharges.reduce(
          (acc, row) => acc + Number(row.gst_amount || 0),
          0
        );
      const cgstAmount =
        (Number(inputChargeRow.cgst) || 0) +
        updatedCharges.reduce((acc, row) => acc + Number(row.cgst || 0), 0);
      const sgstAmount =
        (Number(inputChargeRow.sgst) || 0) +
        updatedCharges.reduce((acc, row) => acc + Number(row.sgst || 0), 0);
      const grandTotal = subTotal + gstAmount;
      setTotals({
        subTotal,
        cgstAmount,
        sgstAmount,
        gstAmount,
        grandTotal,
      });
      return updatedCharges;
    });
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // ---------------- Table Row Handlers ----------------
  const handleAddRow = () => {
    // Default empty numeric fields to 0
    const normalizedRow = {
      containerType: inputRow.containerType,
      weight: inputRow.weight || 0,
      length: inputRow.length || 0,
      breadth: inputRow.breadth || 0,
      height: inputRow.height || 0,
      temperature: inputRow.temperature || 0,
    };
    setTableData((prev) => [...prev, normalizedRow]);
    setInputRow({
      containerType: "",
      weight: "",
      length: "",
      breadth: "",
      height: "",
      temperature: "",
    });
    if (errors.tableData) setErrors((prev) => ({ ...prev, tableData: "" }));
  };

  const handleRemoveRow = (index) => {
    setTableData((prev) => prev.filter((_, i) => i !== index));
  };

   // ---------------- Validation ----------------
  const validate = () => {
    const err = {};

    if (!formData.client_name) err.client_name = "Booking Party Name is required";
    if (!formData.client_company)
      err.client_company = "Booking Company Name is required";
    if (!formData.incoterm) err.incoterm = "Incoterm is required";
    if (!formData.pol) err.pol = "Port of Loading is required";
    if (!formData.pod) err.pod = "Port of Discharge is required";
    if (!formData.freight_terms)
      err.freight_terms = "Freight Terms is required";
    if (!formData.hs_code) err.hs_code = "HS Code is required";
    if (!formData.nature_of_cargo)
      err.nature_of_cargo = "Cargo/HSN Description is required";
    if (!formData.packaging_type)
      err.packaging_type = "Packaging Type is required";
    if (!formData.creation_date)
      err.creation_date = "Creation Date is required";
    if (!formData.valid_till_date)
      err.valid_till_date = "Valid Date is required";
    if (!formData.credit_days) err.credit_days = "Credit Days is required";
    if (!formData.credit_amount)
      err.credit_amount = "Credit Amount is required";
    if (!formData.cha_required) err.cha_required = "CHA Services is required";
    if (!formData.packaging_count)
      err.packaging_count = "Packaging Count is required";
    if (formData.goods_value === undefined || formData.goods_value === null || formData.goods_value === "")
      err.goods_value = "goods_value is required";
    if (!formData.term_and_condition)
      err.term_and_condition = "Term & Conditions is required";

    // Location validation
    if (!selectedLocation) {
      err.selectedLocation = "Location is required";
    }

    const inc = formData.incoterm;
    if (["Door to Door", "ICD to Door"].includes(inc)) {
      if (!formData.place_of_receipt) err.place_of_receipt = "Required";
      if (!formData.place_of_receipt_pin) err.place_of_receipt_pin = "Required";
      if (!formData.final_port_of_delivery) err.final_port_of_delivery = "Required";
      if (!formData.final_port_of_delivery_pin) err.final_port_of_delivery_pin = "Required";
    } else if (["Door to Port", "ICD to Port"].includes(inc)) {
      if (!formData.place_of_receipt) err.place_of_receipt = "Required";
      if (!formData.place_of_receipt_pin) err.place_of_receipt_pin = "Required";
    } else if (inc === "Port to Door") {
if (!formData.final_port_of_delivery) err.final_port_of_delivery = "Required";
       if (!formData.final_port_of_delivery_pin) err.final_port_of_delivery_pin = "Required";
     }

     if (!formData.routing) err.routing = "Routing is required";
     if (
       ["Indirect", "Both"].includes(formData.routing) &&
       !formData.transshipment_hub
     )
       err.transshipment_hub = "Transhipment Hub is required";
     if (!formData.first_shipper_name)
       err.first_shipper_name = "Shipper Name is required";
     if (!formData.first_consignee_name)
       err.first_consignee_name = "Consignee Name is required";

    if (!formData.shipment_segment)
      err.shipment_segment = "Shipment Segment is required";
    if (
      ["FE", "LE"].includes(formData.shipment_segment) &&
      !formData.stuffing_destuffing_At
    )
      err.stuffing = "Stuffing location is required";
    if (
      ["LI", "FI"].includes(formData.shipment_segment) &&
      !formData.stuffing_destuffing_At
    )
      err.destuffing = "Destuffing location is required";

    if (formData.nature_of_cargo === "Hazardous") {
      if (!formData.haz_class) err.haz_class = "HAZ Class is required";
    }

    if (!formData.document_type)
      err.document_type = "Document Type is required";
    if (formData.document_type === "Original" && !formData.document_count)
      err.document_count = "Document count is required";
    if (formData.switch_bl === "Yes" && !formData.switch_at)
      err.switch_at = "Switch At required";

    if (tableData.length === 0) {
      err.tableData = "At least one container detail is required";
    }
    if (chargeRow.length === 0) {
      err.chargeTable = "At least one charge is required";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

// ---------------- Form Submission ----------------
  const submitQuotationData = async () => {
    console.log('submitQuotation called');
    console.log('selectedLocation state:', selectedLocation);
    setLoadingSubmit(true);
    try {
      const data = {
        enq_id: id,
        whatsapp_number: "+917807785161",
        client_name: formData.client_name,
        client_company: formData.client_company,
        cha_required: formData.cha_required,
        pol: formData.pol,
        pod: formData.pod,
        place_of_receipt: formData.place_of_receipt,
        place_of_receipt_pin: formData.place_of_receipt_pin,
        final_port_of_delivery: formData.final_port_of_delivery,
        final_port_of_delivery_pin: formData.final_port_of_delivery_pin,
        shipment_segment: formData.shipment_segment,
        hs_code: formData.hs_code,
        hs_description: formData.hs_description,
        freight_terms: formData.freight_terms,
        incoterm: formData.incoterm,
        nature_of_cargo: formData.nature_of_cargo,
        haz_class: formData.haz_class,
        document_type: formData.document_type,
        document_count: formData.document_count,
        switch_bl: formData.switch_bl,
        switch_at: formData.switch_at,
        stuffing_destuffing: ["FE", "LE"].includes(formData.shipment_segment)
          ? "Stuffing"
          : ["LI", "FI"].includes(formData.shipment_segment)
          ? "Destuffing"
          : "",
        stuffing_destuffing_At: formData.stuffing_destuffing_At,
        packaging_type: formData.packaging_type,
        packaging_count: formData.packaging_count,
        goods_value: formData.goods_value,
        remarks: formData.remarks,
        transshipment_hub: formData.transshipment_hub,
        routing: formData.routing,
        first_shipper_name: formData.first_shipper_name,
        first_consignee_name: formData.first_consignee_name,
        creation_date: formData.creation_date,
        valid_till_date: formData.valid_till_date,
        credit_days: parseInt(formData.credit_days) || 0,
        credit_amount: parseFloat(formData.credit_amount) || 0,
        exchange_rate: 1,
        sub_total: totals.subTotal,
        gst_total: totals.gstAmount,
        grand_total: totals.grandTotal,
        tnc_parameter: formData.term_and_condition,
        Status: "Quotation Sent",
        Status_client: "Quotation Received",
        notificationType: "shared",
        job_location: selectedLocation ? (typeof selectedLocation === 'object' ? selectedLocation.value : selectedLocation) : "",
        containers: tableData.map((c) => ({
          container_id: c.container_id,
          container_type: c.containerType,
          container_weight: c.weight,
          container_length: c.length,
          container_breadth: c.breadth,
          container_height: c.height,
          container_temperature: c.temperature,
        })),
        quotation_charges: chargeRow.map((c) => ({
          charge_code: c.charge_code,
          charge_currency: c.currency,
          amount: c.amount,
          gst_percentage: c.gst,
          quantity: c.quantity,
          exchange_rate: c.exchange_rate,
          tax_amount: c.tax,
          gst_amount: c.gst_amount,
          total_amount: c.total,
        })),
      };
      console.log('Submitting with job_location:', selectedLocation, 'value:', typeof selectedLocation === 'object' ? selectedLocation?.value : selectedLocation);
      console.log('Full data being sent:', JSON.stringify(data));
      const res = await api.post("/quotation/sendQuotation", data);
      console.log(res.data);
      return res.data;
    } catch (err) {
      console.error("Failed to send quotation:", err);
      setError("Failed to send quotation. Please try again.");
      throw err;
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Email sending
  const sendEmail = async () => {
    setEmailLoading(true);
    setEmailError("");
    try {
      await api.post(`/quotation/send-email/${id}`, { recipients: emailRecipients });
      setEmailRecipients("");
    } catch (err) {
      console.error("Failed to send quotation email:", err);
      setEmailError("Failed to send email. Please try again.");
      throw err;
    } finally {
      setEmailLoading(false);
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      const res = await submitQuotationData();
      if (res?.pdfBase64) {
        setQuotationPdf({
          base64: res.pdfBase64,
          filename: `${id}_quotation.pdf`
        });
      }
      if (emailRecipients.trim()) {
        await sendEmail();
      }
      setShowConfirm(false);
      setShowSuccess(true);
    } catch (err) {
      console.error("Submission error:", err);
    }
  };

  const downloadQuotationPDF = () => {
    if (quotationPdf) {
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${quotationPdf.base64}`;
      link.download = quotationPdf.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;
    setShowConfirm(true);
  };
  const containerTypes = [
    "20'Standard",
    "20'Open Top",
    "20'Reefer",
    "20'Flat Rack",
    "20'Platform",
    "20'Flexi Tank",
    "20'ISO Tank",
    "20'Hard Top",
    "20'Collapsible",
    "40'Standard",
    "40'High Cube",
    "40'Open top",
    "40'Reefer",
    "40'Flat Rack",
    "40'Platform",
    "40'Flexi Tank",
    "40'ISO Tank",
    "40'Hard Top",
    "40'Collapsible Flat Rack",
  ];

  const containerOptions = containerTypes.map((type) => ({
    value: type,
    label: type,
  }));

  if (loading) {
    return (
      <div
        className="container-fluid mb-4"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  if (lockError) {
    return (
      <div className="container-fluid mb-4">
        <div className="d-flex align-items-center mb-3">
          <BackButton />
        </div>

        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-7">
            <div className="card border-0 shadow-lg overflow-hidden">
              <div className="card-body text-center p-5">
                {/* Animated Lock Icon */}
                <div className="mb-4 position-relative d-inline-block">
                  <div className="position-absolute top-0 start-0 w-70 h-70 bg-warning rounded-circle opacity-25" style={{transform: 'scale(1.2)'}}></div>
                  <div className="position-relative bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{width: 100, height: 100}}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-warning"
                    >
                      <path
                        d="M12 2C13.1 2 14 2.9 14 4V6H16C17.1 6 18 6.9 18 8V20C18 21.1 17.1 22 16 22H8C6.9 22 6 21.1 6 20V8C6 6.9 6.9 6 8 6H10V4C10 2.9 10.9 2 12 2ZM12 4V6H12V4ZM8 8V20H16V8H8ZM12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                </div>

                {/* Title */}
                <h4 className="fw-bold text-dark mb-2">
                  <i className="fas fa-lock text-warning me-2"></i>
                  Access Restricted
                </h4>

                {/* Locked By Info */}
                <div className="bg-warning bg-opacity-10 border border-warning border-opacity-25 rounded-3 p-3 mb-4">
                  <p className="text-muted mb-1 small text-uppercase fw-semibold" style={{letterSpacing: '0.5px'}}>Currently being edited by</p>
                  <p className="fw-bold text-dark mb-0 fs-5">
                    {lockedByName || "Another team member"}
                  </p>
                  {lockedBy && !lockedByName && (
                    <p className="text-muted small mb-0 mt-1">User ID: {lockedBy}</p>
                  )}
                </div>

                {/* Description */}
                <p className="text-muted mb-4">
                  Looks like someone from our team is working on it.To prevent data conflicts, please wait until they finish editing.
                </p>

              

                {/* Action Buttons */}
                <div className="d-flex flex-column gap-2">
                  <button
                    className="btn btn-warning text-dark fw-semibold shadow-sm"
                    onClick={() => window.location.reload()}
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    Try Again
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => window.history.back()}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Go Back
                  </button>
                </div>
              </div>
            </div>

            {/* Footer tip */}
            <div className="text-center mt-3">
              <small className="text-muted">
                <i className="fas fa-clock me-1"></i>
                Locks expire automatically after 30 minutes of inactivity
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <p>{error}</p>;
  }

  return (
    <>
      <BackButton />

      <form onSubmit={handleSubmit}>
        <div className="row mb-3 g-3">
          <div className="col-sm-6 col-md-4">
            <InputField
              label="Booking Party Name"
              clsname="required"
              value={formData.client_name}
              onChange={(e) => handleFormChange("client_name", e.target.value)}
              error={errors.client_name}
            />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField
              label="Booking Company Name"
              clsname="required"
              value={formData.client_company}
              onChange={(e) =>
                handleFormChange("client_company", e.target.value)
              }
              error={errors.client_company}
            />
          </div>
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="CHA Services Required?"
              options={["Yes", "No"]}
              clsname="required"
              value={formData.cha_required}
              onChange={(val) => handleFormChange("cha_required", val)}
              error={errors.cha_required}
            />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField
              label="Creation Date"
              clsname="required"
              value={formatDateToDDMMYYYY(new Date().toISOString())}
              onChange={(e) =>
                handleFormChange(
                  "creation_date",
                  parseDDMMYYYYToYYYYMMDD(e.target.value)
                )
              }
              error={errors.creation_date}
            />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField
              label="Valid till Date"
              clsname="required"
              type="date"
              value={formData.valid_till_date}
              onChange={(e) =>
                handleFormChange("valid_till_date", e.target.value)
              }
              error={errors.valid_till_date}
            />
          </div>
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="Incoterm"
              options={incoterms}
              clsname="required"
              value={formData.incoterm}
              onChange={(val) => handleFormChange("incoterm", val)}
              error={errors.incoterm}
            />
          </div>
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="Port of Loading"
              options={portOptions}
              clsname="required"
              value={formData.pol}
              onChange={(val) => handleFormChange("pol", val)}
              error={errors.pol}
              isLoading={loadingPorts}
              searchable={true}
            />
          </div>
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="Port of Discharge"
              options={portOptions}
              clsname="required"
              value={formData.pod}
              onChange={(val) => handleFormChange("pod", val)}
              error={errors.pod}
              isLoading={loadingPorts}
              searchable={true}
            />
          </div>
          <div className="col-sm-6 col-md-4 ">
            <SelectField
              label="Freight Terms"
              options={freight_terms}
              clsname="required"
              value={formData.freight_terms}
              onChange={(val) => handleFormChange("freight_terms", val)}
              error={errors.freight_terms}
            />
          </div>

          {formData.incoterm === "Door to Door" && (
            <>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="Place Of Receipt"
                  value={formData.place_of_receipt}
                  onChange={(e) => handleFormChange("place_of_receipt", e.target.value)}
                  error={errors.place_of_receipt}
                />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="POR Pin Code"
                  value={formData.place_of_receipt_pin}
                  onChange={(e) =>
                    handleFormChange("place_of_receipt_pin", e.target.value)
                  }
                  error={errors.place_of_receipt_pin}
                />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="Final Port Of Delivery"
                  value={formData.final_port_of_delivery}
                  onChange={(e) => handleFormChange("final_port_of_delivery", e.target.value)}
                  error={errors.final_port_of_delivery}
                />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="FPOD Pin Code"
                  value={formData.final_port_of_delivery_pin}
                  onChange={(e) =>
                    handleFormChange("final_port_of_delivery_pin", e.target.value)
                  }
                  error={errors.final_port_of_delivery_pin}
                />
              </div>
            </>
          )}

          {formData.incoterm === "Door to Port" && (
            <>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="Place Of Receipt"
                  value={formData.place_of_receipt}
                  onChange={(e) => handleFormChange("place_of_receipt", e.target.value)}
                  error={errors.place_of_receipt}
                />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="POR Pin Code"
                  value={formData.place_of_receipt_pin}
                  onChange={(e) =>
                    handleFormChange("place_of_receipt_pin", e.target.value)
                  }
                  error={errors.place_of_receipt_pin}
                />
              </div>
            </>
          )}
          {formData.incoterm === "Port to Door" && (
            <>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="Final Port Of Delivery"
                  value={formData.final_port_of_delivery}
                  onChange={(e) => handleFormChange("final_port_of_delivery", e.target.value)}
                  error={errors.final_port_of_delivery}
                />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="FPOD Pin Code"
                  value={formData.final_port_of_delivery_pin}
                  onChange={(e) =>
                    handleFormChange("final_port_of_delivery_pin", e.target.value)
                  }
                  error={errors.final_port_of_delivery_pin}
                />
              </div>
            </>
          )}
          {formData.incoterm === "ICD to Port" && (
            <>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="Place Of Receipt"
                  value={formData.place_of_receipt}
                  onChange={(e) => handleFormChange("place_of_receipt", e.target.value)}
                  error={errors.place_of_receipt}
                />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="POR Pin Code"
                  value={formData.place_of_receipt_pin}
                  onChange={(e) =>
                    handleFormChange("place_of_receipt_pin", e.target.value)
                  }
                  error={errors.place_of_receipt_pin}
                />
              </div>
            </>
          )}

          {formData.incoterm === "ICD to Door" && (
            <>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="Place Of Receipt"
                  value={formData.place_of_receipt}
                  onChange={(e) => handleFormChange("place_of_receipt", e.target.value)}
                  error={errors.place_of_receipt}
                />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="POR Pin Code"
                  value={formData.place_of_receipt_pin}
                  onChange={(e) =>
                    handleFormChange("place_of_receipt_pin", e.target.value)
                  }
                  error={errors.place_of_receipt_pin}
                />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="Final Port Of Delivery"
                  value={formData.final_port_of_delivery}
                  onChange={(e) => handleFormChange("final_port_of_delivery", e.target.value)}
                  error={errors.final_port_of_delivery}
                />
              </div>
              <div className="col-sm-6 col-md-4">
                <InputField
                  label="FPOD Pin Code"
                  value={formData.final_port_of_delivery_pin}
                  onChange={(e) =>
                    handleFormChange("final_port_of_delivery_pin", e.target.value)
                  }
                  error={errors.final_port_of_delivery_pin}
                />
              </div>
            </>
          )}

          {/* Shipping Segment */}
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="Shipment Segment"
              options={shipment_segments}
              clsname="required"
              value={formData.shipment_segment}
              onChange={(val) => handleFormChange("shipment_segment", val)}
              error={errors.shipment_segment}
            />
          </div>

          {/* Location for Job Number */}
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="Select Location"
              clsname="required"
              options={locationOptions}
              value={selectedLocation}
              onChange={(val) => {
                console.log('Location changed to:', val);
                setSelectedLocation(val);
                if (errors.selectedLocation) setErrors(prev => ({ ...prev, selectedLocation: "" }));
              }}
              placeholder="Select Location"
              error={errors.selectedLocation}
            />
          </div>
          {["FE", "LE"].includes(formData.shipment_segment) && (
            <div className="col-sm-6 col-md-4">
              <InputField
                label="Stuffing At"
                placeholder="Enter Stuffing Location"
                clsname="required"
                value={formData.stuffing_destuffing_At}
                onChange={(e) =>
                  handleFormChange("stuffing_destuffing_At", e.target.value)
                }
                error={errors.stuffing}
              />
            </div>
          )}
          {["LI", "FI"].includes(formData.shipment_segment) && (
            <div className="col-sm-6 col-md-4">
              <InputField
                label="Destuffing At"
                placeholder="Enter Destuffing Location"
                clsname="required"
                value={formData.stuffing_destuffing_At}
                onChange={(e) =>
                  handleFormChange("stuffing_destuffing_At", e.target.value)
                }
                error={errors.destuffing}
              />
            </div>
          )}

          {/* Nature of Cargo */}
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="Nature of Cargo"
              options={[
                "Hazardous",
                "Non Hazardous",
                "Temperature Control",
                "Moisture Control",
                "Heavy Duty",
                "Over Dimensional Cargo",
              ]}
              clsname="required"
              value={formData.nature_of_cargo}
              onChange={(val) => handleFormChange("nature_of_cargo", val)}
              error={errors.nature_of_cargo}
            />
          </div>
          {formData.nature_of_cargo === "Hazardous" && (
            <>
              <div className="col-sm-6 col-md-4">
                <SelectField
                  label="HAZ Class"
                  options={[
                    "Class 1",
                    "Class 2",
                    "Class 3",
                    "Class 4",
                    "Class 5",
                    "Class 6",
                    "Class 7",
                    "Class 8",
                  ]}
                  clsname="required"
                  value={formData.haz_class}
                  onChange={(val) => handleFormChange("haz_class", val)}
                  error={errors.haz_class}
                />
              </div>
            </>
          )}

          {/* Document Type */}
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="Document Type"
              options={["Original", "Seaway"]}
              clsname="required"
              value={formData.document_type}
              onChange={(val) => handleFormChange("document_type", val)}
              error={errors.document_type}
            />
          </div>
          {formData.document_type === "Original" && (
            <div className="col-sm-6 col-md-4">
              <InputField
                label="Document Count"
                type="number"
                placeholder="Enter document count"
                value={formData.document_count}
                onChange={(e) =>
                  handleFormChange("document_count", e.target.value)
                }
                error={errors.document_count}
              />
            </div>
          )}
          {/* Switch BL Dropdown */}
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="Switch BL"
              options={["No", "Yes"]}
              value={formData.switch_bl || "No"}
              onChange={(val) => handleFormChange("switch_bl", val)}
              error={errors.switch_bl}
            />
          </div>
          {formData.switch_bl === "Yes" && (
            <div className="col-sm-6 col-md-4">
              <SelectField
                label="Switch At"
                options={portOptions}
                value={formData.switch_at}
                onChange={(val) => handleFormChange("switch_at", val)}
                error={errors.switch_at}
                isLoading={loadingPorts}
                searchable={true}
              />
            </div>
          )}

          {/* HS Code + Description */}
          <div className="col-sm-6 col-md-4">
            <InputField
              label="HS Code"
              placeholder="Enter HS Code"
              clsname="required"
              value={formData.hs_code}
              onChange={(e) => handleFormChange("hs_code", e.target.value)}
              error={errors.hs_code}
            />
          </div>
          <div className="col-sm-6 col-md-4 ">
            <InputField
              label="Cargo/HSN Description"
              placeholder="Enter Description"
              value={formData.hs_description}
              onChange={(e) =>
                handleFormChange("hs_description", e.target.value)
              }
              error={errors.hs_description}
            />
          </div>

          <div className="col-sm-6 col-md-4">
            <InputField
              label="Packaging Type"
              clsname="required"
              placeholder="Enter Packaging Type"
              value={formData.packaging_type}
              onChange={(e) =>
                handleFormChange("packaging_type", e.target.value)
              }
              error={errors.packaging_type}
            />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField
              label="Packaging Count"
              type="number"
              clsname="required"
              placeholder="Enter Count"
              value={formData.packaging_count}
              onChange={(e) =>
                handleFormChange("packaging_count", e.target.value)
              }
              error={errors.packaging_count}
            />
          </div>

          <div className="col-sm-6 col-md-4">
            <InputField
              label="Credit Days"
              type="number"
              clsname="required"
              placeholder="Enter Count"
              value={formData.credit_days}
              onChange={(e) => handleFormChange("credit_days", e.target.value)}
              error={errors.credit_days}
            />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField
              label="Credit Amount"
              type="number"
              clsname="required"
              placeholder="Enter Count"
              value={formData.credit_amount}
              onChange={(e) =>
                handleFormChange("credit_amount", e.target.value)
              }
              error={errors.credit_amount}
            />
          </div>
           <div className="col-md-4">
            <InputField
              label="Goods Value"
              type="number"
              clsname="required"
              placeholder="Enter Goods Value"
              value={formData.goods_value}
              onChange={(e) => handleFormChange("goods_value", e.target.value)}
              error={errors.goods_value}
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Remarks"
              placeholder="Any remarks or additional info"
              value={formData.remarks}
              onChange={(e) => handleFormChange("remarks", e.target.value)}
            />
          </div>
          {/* Routing */}
          <div className="col-sm-6 col-md-4">
            <SelectField
              label="Routing"
              options={routing_options}
              clsname="required"
              value={formData.routing}
              onChange={(val) => handleFormChange("routing", val)}
              error={errors.routing}
            />
          </div>
          {["Indirect", "Both"].includes(formData.routing) && (
            <div className="col-sm-6 col-md-4">
              <SelectField
                label="Transhipment Hub"
                options={portOptions}
                clsname="required"
                value={formData.transshipment_hub}
                onChange={(val) =>
                  handleFormChange("transshipment_hub", val)
                }
                error={errors.transshipment_hub}
                isLoading={loadingPorts}
                searchable={true}
              />
            </div>
          )}
          {/* Shipper Name + Consignee Name */}
          <div className="col-sm-6 col-md-4">
            <InputField
              label="Shipper Name"
              placeholder="Enter Shipper Name"
              clsname="required"
              value={formData.first_shipper_name}
              onChange={(e) =>
                handleFormChange("first_shipper_name", e.target.value)
              }
              error={errors.first_shipper_name}
            />
          </div>
          <div className="col-sm-6 col-md-4">
            <InputField
              label="Consignee Name"
              placeholder="Enter Consignee Name"
              clsname="required"
              value={formData.first_consignee_name}
              onChange={(e) =>
                handleFormChange("first_consignee_name", e.target.value)
              }
              error={errors.first_consignee_name}
            />
          </div>
        </div>

        <div
          className="mb-4 position-relative fullwidth"
          style={{ maxHeight: "100%" }}
        >
          <table className="table table-bordered tbllbl mb-2 mb-md-3">
            <thead className="table-light">
              <tr>
{formData.shipment_segment !== "LE" &&
                   formData.shipment_segment !== "LI" && (
                     <th>Container Type</th>
                   )}
                 {formData.shipment_segment !== "LE" &&
                   formData.shipment_segment !== "LI" && (
                     <th>Gross Cargo Weight</th>
                   )}
                 {!["FE", "FI"].includes(formData.shipment_segment) && (
                   <th>Length</th>
                 )}
                 {!["FE", "FI"].includes(formData.shipment_segment) && (
                   <th>Breadth</th>
                 )}
                 {!["FE", "FI"].includes(formData.shipment_segment) && (
                   <th>Height</th>
                 )}
                 {["LE", "LI"].includes(formData.shipment_segment) && (
                   <th>CBM</th>
                 )}
                 {formData.nature_of_cargo === "Temperature Control" && (
                  <th>Temperature</th>
                )}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                  {formData.shipment_segment !== "LE" &&
                    formData.shipment_segment !== "LI" && (
                      <td>
                        <Select
                          value={
                            inputRow.containerType
                              ? {
                                  value: inputRow.containerType,
                                  label: inputRow.containerType,
                                }
                              : null
                          }
                          onChange={(opt) =>
                            handleInputChange("containerType", opt?.value)
                          }
                          options={containerOptions}
                          placeholder="Select Container"
                          isClearable
                        />
                      </td>
                    )}
                {formData.shipment_segment !== "LE" &&
                  formData.shipment_segment !== "LI" && (
                    <td>
                      <InputField
                        value={inputRow.weight}
                        type="number"
                        placeholder="Gross Cargo Weight in kg's"
                        onChange={(e) =>
                          handleInputChange("weight", e.target.value)
                        }
                      />
                    </td>
                  )}
                {!["FE", "FI"].includes(formData.shipment_segment) && (
                  <td>
                    <InputField
                      value={inputRow.length}
                      type="number"
                      placeholder="Length in cm's"
                      onChange={(e) =>
                        handleInputChange("length", e.target.value)
                      }
                    />
                  </td>
                )}
                {!["FE", "FI"].includes(formData.shipment_segment) && (
                  <td>
                    <InputField
                      value={inputRow.breadth}
                      type="number"
                      placeholder="Breadth in cm's"
                      onChange={(e) =>
                        handleInputChange("breadth", e.target.value)
                      }
                    />
                  </td>
                )}
                {!["FE", "FI"].includes(formData.shipment_segment) && (
                  <td>
                    <InputField
                      value={inputRow.height}
                      type="number"
                      placeholder="Height in cm's"
                      onChange={(e) =>
                        handleInputChange("height", e.target.value)
                      }
                    />
                  </td>
                )}
                {["LE", "LI"].includes(formData.shipment_segment) && (
                  <td>
                    {inputRow.length && inputRow.breadth && inputRow.height
                      ? (
                          (parseFloat(inputRow.length) *
                            parseFloat(inputRow.breadth) *
                            parseFloat(inputRow.height)) /
                          1000000
                        ).toFixed(2)
                      : "0.00"}
                  </td>
                )}
                {formData.nature_of_cargo === "Temperature Control" && (
                  <td>
                    <InputField
                      value={inputRow.temperature}
                      type="number"
                      placeholder="Temperature in °C"
                      onChange={(e) =>
                        handleInputChange("temperature", e.target.value)
                      }
                    />
                  </td>
                )}
                <td className="text-center">
                  <AddCircleOutlineIcon
                    style={{
                      cursor: "pointer",
                      color: "green",
                      fontSize: 28,
                    }}
                    onClick={handleAddRow}
                  />
                </td>
              </tr>
               {tableData.map((row, index) => (
                 <tr key={index}>
                   {!["LE", "LI"].includes(formData.shipment_segment) && (
                     <td>{row.containerType}</td>
                   )}
                   {!["LE", "LI"].includes(formData.shipment_segment) && (
                     <td>{row.weight}</td>
                   )}
                   {!["FE", "FI"].includes(formData.shipment_segment) && (
                     <td>{row.length}</td>
                   )}
                   {!["FE", "FI"].includes(formData.shipment_segment) && (
                     <td>{row.breadth}</td>
                   )}
                   {!["FE", "FI"].includes(formData.shipment_segment) && (
                     <td>{row.height}</td>
                   )}
                   {["LE", "LI"].includes(formData.shipment_segment) && (
                      <td>
                        {row.length && row.breadth && row.height
                          ? (
                              (parseFloat(row.length) *
                                parseFloat(row.breadth) *
                                parseFloat(row.height)) /
                              1000000
                            ).toFixed(2)
                          : "0.00"}
                      </td>
                    )}
                    {formData.nature_of_cargo === "Temperature Control" && (
                    <td>{row.temperature}</td>
                  )}
                  <td className="text-center">
                    <DeleteOutlineIcon
                      style={{
                        cursor: "pointer",
                        color: "red",
                        fontSize: 28,
                      }}
                      onClick={() => handleRemoveRow(index)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-error text-danger mb-2">
            {errors.tableData && <span>{errors.tableData}</span>}
          </div>
        </div>
        <div className="d-flex " style={{ gap: "1.5rem" }}>
          <div
            className="mb-4 position-relative fullwidth"
            style={{ maxHeight: "100%" }}
          >
            <table className="table table-bordered tbllbl mb-2 mb-md-3">
              <thead className="table-light">
                <tr>
                  <th>Charge Code</th>
                  <th>Currency</th>
                  <th>Amount</th>
                  <th>GST%</th>
                  <th>Quantity</th>
                  <th>Tax(INR)</th>
                  <th>GST Amount</th>
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <SelectField
                      options={chargeCodeOptions}
                      value={inputChargeRow.charge_code}
                      onChange={(val) => {
                        handleInputChargeChange("charge_code", val);
                        fetchGSTForChargeCode(val, "input");
                      }}
                      isLoading={loadingChargeCodes}
                      searchable
                    />
                  </td>
                  <td className="minselect">
                    <Select
                      options={currencyOptions}
                      value={
                        currencyOptions.find(
                          (c) => c.value === inputChargeRow.currency
                        ) || null
                      }
                      onChange={(selected) =>
                        handleCurrencyChange("input", selected)
                      }
                      isLoading={loadingCurrencies}
                    />
                  </td>
                  <td>
                    <InputField
                      type="text"
                      placeholder="Amount"
                      value={
                        inputChargeRow.amount === 0 ? "" : inputChargeRow.amount
                      }
                      onChange={(e) =>
                        handleInputChargeChange("amount", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <InputField
                      type="text"
                      value={inputChargeRow.gst }
                      readOnly
                      onChange={(e) =>
                        handleInputChargeChange("gst", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <InputField
                      type="text"
                      placeholder="Quantity"
                      value={
                        inputChargeRow.quantity === 0
                          ? ""
                          : inputChargeRow.quantity
                      }
                      onChange={(e) =>
                        handleInputChargeChange("quantity", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <InputField
                      type="text"
                      value={inputChargeRow.tax}
                      onChange={(e) =>
                        handleInputChargeChange("tax", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <InputField
                      type="text"
                      value={inputChargeRow.gst_amount}
                      onChange={(e) =>
                        handleInputChargeChange("gst_amount", e.target.value)
                      }
                    />
                  </td>
                  <td>{inputChargeRow.total}</td>
                  <td className="text-center">
                    <AddCircleOutlineIcon
                      style={{
                        cursor: "pointer",
                        color: "green",
                        fontSize: 28,
                      }}
                      onClick={addChargeRow}
                    />
                  </td>
                </tr>
                {chargeRow.map((charge, index) => (
                  <tr key={index}>
                    <td>
                      <SelectField
                        options={chargeCodeOptions}
                        value={charge.charge_code}
                        onChange={(val) => {
                          handleChargeInputChange(index, "charge_code", val);
                          fetchGSTForChargeCode(val, index);
                        }}
                        isLoading={loadingChargeCodes}
                        searchable
                      />
                    </td>
                    <td className="minselect">
                      <Select
                        options={currencyOptions}
                        value={
                          currencyOptions.find(
                            (c) => c.value === charge.currency
                          ) || null
                        }
                        onChange={(selected) =>
                          handleCurrencyChange(index, selected)
                        }
                        isLoading={loadingCurrencies}
                      />
                    </td>
                    <td>
                      <InputField
                        type="text"
                        value={charge.amount}
                        onChange={(e) =>
                          handleChargeInputChange(
                            index,
                            "amount",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <InputField
                        type="text"
                        value={charge.gst}
                        readOnly
                        onChange={(e) =>
                          handleChargeInputChange(index, "gst", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <InputField
                        type="text"
                        value={charge.quantity}
                        onChange={(e) =>
                          handleChargeInputChange(
                            index,
                            "quantity",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>
                      <InputField
                        type="text"
                        value={charge.tax}
                        onChange={(e) =>
                          handleChargeInputChange(index, "tax", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <InputField
                        type="text"
                        value={charge.gst_amount}
                        onChange={(e) =>
                          handleChargeInputChange(
                            index,
                            "gst_amount",
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td>{charge.total}</td>
                    <td className="text-center">
                      <DeleteOutlineIcon
                        style={{
                          cursor: "pointer",
                          color: "red",
                          fontSize: 28,
                        }}
                        onClick={() => deleteChargeRow(index)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="table-error text-danger mb-2">
              {errors.chargeTable && <span>{errors.chargeTable}</span>}
            </div>
          </div>
          <div
            className="mb-4 position-relative fullwidth"
            style={{ maxHeight: "100%" }}
          >
            <table className="table table-bordered tbllbl mb-2 mb-md-3">
              <thead className="table-light">
                <tr>
                  <th>Exchange Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                   <span>
                        1&nbsp;{inputChargeRow.currency || "-"}&nbsp;=&nbsp;
                        {inputChargeRow.exchange_rate || 0}
                        {inputChargeRow.currency ? " INR" : ""}
                   </span>
                  </td>
                </tr>
                {chargeRow.map((charge, index) => (
                  <tr key={index}>
                    <td>
                      {charge.currency ? (
                        <span>
                          1 {charge.currency || "-"} ={" "}
                          {charge.exchange_rate || "-"} INR
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="lstbox flex-column d-inline-flex gap-3">
          <div className="d-flex justify-content-between gap-5">
            <div>
              <p>
                <strong className="me-2">Sub Total :</strong> {totals.subTotal}
              </p>
            </div>
            <div>
              <p>
                <strong>GST Breakup</strong>
              </p>
              <p>CGST: {chargeRow.map((row) => row.gst / 2 + "%").join(" ")}</p>
              <p>SGST: {chargeRow.map((row) => row.gst / 2 + "%").join(" ")}</p>
              <p>
                <strong>
                  <span className="me-2">GST Total : </span>
                  {totals.gstAmount}
                </strong>
              </p>
            </div>
          </div>
          <p>
            <strong className="me-2">Grand Total :</strong> {totals.grandTotal}
          </p>
          <SelectField
            label="Term & Conditions"
            options={[
              "LCL IMPORT - NON HAZ CARGO",
              "LCL IMPORT - HAZ CARGO",
              "LCL EXPORT - NON HAZ CARGO",
              "LCL EXPORT- HAZ CARGO",
              "FCL IMPORT - NON HAZ CARGO ( Dock Stuffing )",
              "FCL IMPORT - NON HAZ CARGO ( Factory Stuffing )",
              "FCL IMPORT - HAZ CARGO ( Dock Stuffing )",
              "FCL IMPORT - HAZ CARGO ( Factory Stuffing )",
              "FCL EXPORT- NON HAZ CARGO ( Dock Stuffing )",
              "FCL EXPORT- NON HAZ CARGO ( Factory Stuffing )",
              "FCL EXPORT- HAZ CARGO ( Dock Stuffing )",
              "FCL EXPORT- HAZ CARGO ( Factory Stuffing )",
              "FCL EXPORT- Russia / CIS Sector Via Iran",
            ]}
            value={formData.term_and_condition}
            onChange={(val) => handleFormChange("term_and_condition", val)}
            error={errors.term_and_condition}
          />
        </div>

        <div className="text-end">
          <Button
            type="submit"
            text="Send Quotation"
            variant="success"
            loading={loadingSubmit}
          />
        </div>
      </form>

      {showConfirm && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Send Quotation</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConfirm(false)}
                  disabled={loadingSubmit}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to send the quotation?</p>
                <div className="mb-3">
                  <label htmlFor="emailRecipients" className="form-label">
                    Email Addresses (comma-separated) - Optional
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="emailRecipients"
                    value={emailRecipients}
                    onChange={(e) => setEmailRecipients(e.target.value)}
                    placeholder="e.g., user1@example.com, user2@example.com"
                  />
                  {emailError && (
                    <div className="text-danger mt-1">{emailError}</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <Button
                  className="btn btn-secondary"
                  onClick={() => setShowConfirm(false)}
                  text="No"
                  disabled={loadingSubmit}
                />
                <Button
                  className="btn btn-primary"
                  onClick={handleConfirmSubmit}
                  disabled={loadingSubmit || emailLoading}
                  text={
                    loadingSubmit || emailLoading ? (
                      <>
                        <Loader /> Sending...
                      </>
                    ) : (
                      "Yes"
                    )
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
 
      {showSuccess && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Success</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSuccess(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Quotation sent successfully!</p>
              </div>
              <div className="modal-footer">
                <Button
                  type="button"
                  className="btn btn-secondary"
                  onClick={downloadQuotationPDF}
                  text="Download Quotation"
                  disabled={!quotationPdf}
                />
                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowSuccess(false);
                    navigate("/freight/pending-enquiries");
                  }}
                  text="Ok"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {(showConfirm || showSuccess) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
};

export default SendQuotation;
